import { useRef, useState } from "react";
import { Loader2, Upload, ShieldX, ShieldCheck, UserX, UserCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const DESCARTE = "descartada_falso_positivo";
const CONFIRMA = "confirmada";

function OpcionDecision({ activa, onClick, Icon, titulo, texto, tono, testid }) {
  const tonos = {
    verde: activa ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : "hover:border-emerald-300",
    rojo: activa ? "border-red-500 bg-red-50 ring-2 ring-red-200" : "hover:border-red-300",
  };
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex flex-1 items-start gap-2 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors duration-150 ${tonos[tono]}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tono === "verde" ? "text-emerald-600" : "text-red-600"}`} />
      <span>
        <span className="block text-sm font-semibold text-slate-900">{titulo}</span>
        <span className="block text-xs text-slate-500">{texto}</span>
      </span>
    </button>
  );
}

function Evidencia({ matchId, evidencia, setEvidencia }) {
  const ref = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const subir = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/screening/matches/${matchId}/evidencia`, fd);
      setEvidencia(data);
      toast.success("Evidencia cargada al expediente");
    } catch (e) {
      toast.error(apiError(e));
      if (ref.current) ref.current.value = "";
    } finally { setSubiendo(false); }
  };
  return (
    <div>
      <Label className="mb-1 block text-xs">Evidencia (opcional · PDF o imagen)</Label>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg" data-testid="dictamen-evidencia-input"
          className="hidden" disabled={subiendo} onChange={(e) => subir(e.target.files?.[0])} />
        <Button type="button" variant="outline" size="sm" disabled={subiendo} data-testid="dictamen-evidencia-btn"
          onClick={() => ref.current?.click()} className="gap-1.5">
          {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {evidencia ? "Cambiar archivo" : "Elegir archivo"}
        </Button>
        {evidencia && (
          <span data-testid="dictamen-evidencia-nombre" className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> {evidencia.nombre_archivo}
          </span>
        )}
      </div>
    </div>
  );
}

function DictamenCritico({ ficha, onDecided }) {
  const min = ficha.justificacion_min || 30;
  const [decision, setDecision] = useState(null);
  const [motivos, setMotivos] = useState([]);
  const [motivoOtro, setMotivoOtro] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [evidencia, setEvidencia] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const largo = justificacion.trim().length;
  const valido = decision === DESCARTE ? motivos.length > 0 && largo >= min : decision === CONFIRMA && largo > 0;
  const toggle = (k) => setMotivos((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const registrar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/screening/matches/${ficha.match.id}/decidir`, {
        decision, motivos, motivo_otro: motivoOtro, justificacion, evidencia_id: evidencia?.id || null,
      });
      toast.success(`Dictamen registrado · constancia ${data?.constancia?.folio || ""}`);
      onDecided?.(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnviando(false); }
  };

  return (
    <section data-testid="dictamen-form" className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">Dictamen del Oficial de Cumplimiento</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <OpcionDecision activa={decision === DESCARTE} onClick={() => setDecision(DESCARTE)} Icon={UserX} tono="verde"
          testid="dictamen-opcion-descartar" titulo="Descartar — no es la misma persona (homónimo)"
          texto="Libera la retención por esta coincidencia y queda constancia." />
        <OpcionDecision activa={decision === CONFIRMA} onClick={() => setDecision(CONFIRMA)} Icon={UserCheck} tono="rojo"
          testid="dictamen-opcion-confirmar" titulo="Confirmar — es la misma persona"
          texto="Lista de sanción: bloquea. PEP u otra crítica: riesgo alto y caso PLD." />
      </div>
      {decision === DESCARTE && (
        <div data-testid="dictamen-motivos-box">
          <Label className="mb-1.5 block text-xs">Motivos (al menos uno)</Label>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {ficha.motivos_catalogo.map((m) => (
              <label key={m.clave} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <Checkbox data-testid={`dictamen-motivo-${m.clave}`} checked={motivos.includes(m.clave)}
                  onCheckedChange={() => toggle(m.clave)} />
                {m.etiqueta}
              </label>
            ))}
          </div>
          {motivos.includes("otro") && (
            <Input data-testid="dictamen-motivo-otro-input" className="mt-2" placeholder="Describe el otro motivo"
              value={motivoOtro} onChange={(e) => setMotivoOtro(e.target.value)} />
          )}
        </div>
      )}
      {decision === CONFIRMA && (
        <p data-testid="dictamen-efectos-confirmar" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <ShieldX className="mt-0.5 h-4 w-4 shrink-0" />
          Lista de sanción → el cliente queda BLOQUEADO. PEP u otra lista crítica → riesgo ALTO y caso PLD; un PEP
          confirmado sigue requiriendo tu autorización para operar.
        </p>
      )}
      {decision && (
        <div>
          <Label className="mb-1 block text-xs">
            Justificación {decision === DESCARTE ? `(mínimo ${min} caracteres)` : "(obligatoria)"}
          </Label>
          <Textarea data-testid="dictamen-justificacion" rows={3} value={justificacion}
            onChange={(e) => setJustificacion(e.target.value)}
            placeholder={decision === DESCARTE ? "Qué datos de identidad prueban que no es la misma persona…" : "Sustento de la confirmación…"} />
          {decision === DESCARTE && (
            <p data-testid="dictamen-justificacion-contador" className={`mt-1 text-[11px] ${largo >= min ? "text-emerald-600" : "text-slate-400"}`}>
              {largo}/{min} caracteres
            </p>
          )}
        </div>
      )}
      {decision === DESCARTE && <Evidencia matchId={ficha.match.id} evidencia={evidencia} setEvidencia={setEvidencia} />}
      {decision && (
        <div className="flex justify-end">
          <Button data-testid="dictamen-registrar-btn" onClick={registrar} disabled={!valido || enviando}
            className={decision === CONFIRMA ? "bg-red-600 text-white hover:bg-red-700" : "bg-primary hover:bg-sidebar-hover"}>
            {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Registrar dictamen
          </Button>
        </div>
      )}
    </section>
  );
}

function DictamenNoCritico({ ficha, onDecided }) {
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const decidir = async (decision) => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/screening/matches/${ficha.match.id}/decidir`, { decision, comentario });
      toast.success(decision === CONFIRMA ? "Coincidencia confirmada" : "Coincidencia descartada como falso positivo");
      onDecided?.(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnviando(false); }
  };
  return (
    <section data-testid="dictamen-form-no-critica" className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
      <div>
        <Label className="mb-1 block text-xs">Comentario / justificación</Label>
        <Textarea data-testid="match-comentario" rows={3} value={comentario}
          onChange={(e) => setComentario(e.target.value)} placeholder="Sustento de la decisión..." />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button data-testid="match-descartar-btn" variant="outline" disabled={enviando} onClick={() => decidir(DESCARTE)}>
          Descartar (falso positivo)
        </Button>
        <Button data-testid="match-confirmar-btn" className="bg-red-600 text-white hover:bg-red-700" disabled={enviando}
          onClick={() => decidir(CONFIRMA)}>
          {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirmar coincidencia
        </Button>
      </div>
    </section>
  );
}

export function DictamenForm({ ficha, onDecided }) {
  if (ficha.requiere_oc && !ficha.puede_decidir) {
    return (
      <p data-testid="dictamen-solo-oc" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Coincidencia crítica: solo el Oficial de Cumplimiento puede dictaminarla.
      </p>
    );
  }
  return ficha.requiere_oc
    ? <DictamenCritico ficha={ficha} onDecided={onDecided} />
    : <DictamenNoCritico ficha={ficha} onDecided={onDecided} />;
}
